<script setup>
import { Overlay } from 'ol'
import { boundingExtent, buffer } from 'ol/extent'
import { onBeforeUnmount, onMounted, ref, useId } from 'vue'
import { createFeatures } from '../../services/map/FeatureService'
import { createLayer } from '../../services/map/LayerService'

const props = defineProps({
  layers: Array,
  map: Object,
  service: String,
})

const id = useId()
const open = ref(false)
const popupWrapper = ref(null)
const popup = ref(null)
const popover = ref(null)
const element = ref(null)
const offset = 0
const results = ref([])
const resultsIndex = ref(0)
const feature = ref(null)
const properties = ref({})
const title = ref('Feature Info')
const layer = createLayer({
  type: 'FeatureCollection',
  features: [],
})

onMounted(() => {
  popup.value = new Overlay({
    element: popupWrapper.value,
    positioning: 'center-center',
    offset: [0, -offset],
  })
  props.map.addOverlay(popup.value)
  element.value = popup.value.getElement()

  enable()
})

onBeforeUnmount(() => {
  disable()
})

const enable = () => {
  props.map.on('singleclick', clicked)
  props.map.addLayer(layer)
}
const disable = () => {
  props.map.un('singleclick', clicked)
  hide()
  props.map.removeLayer(layer)
}

const clicked = async evt => {
  hide()
  const coords = evt.coordinate
  const layers = props.layers.filter(layer => {
    const data = layer.getData(evt.pixel)
    // transparent pixels have zero for data[3]
    //return data && data.length && data[3] > 0 && layer.get('conf').type === 'TileWMS' && layer.getVisible()
    return (
      data &&
      data.length &&
      layer.get('conf').type === 'TileWMS' &&
      layer.getVisible()
    )
  })
  if (layers && layers.length) {
    const promises = layers.map(layer => {
      return new Promise((resolve, reject) => {
        const layerName = layer.get('title')
        const format = layer.getSource().getParams().INFO_FORMAT
        const geometryName =
          layer.getSource().getParams().GEOM_NAME || 'geometry'

        let url = null
        if (props.service === 'WMS') {
          url = prepareWMSGetFeatureInfoUrl(coords, layer)
        } else if (props.service === 'WFS') {
          url = prepareWFSGetFeatureUrl(coords, layer)
        } else {
          reject('Missing url')
        }
        if (url) {
          requestFeatureInfo(url, result => {
            if (result) {
              let type = props.service.toLowerCase()
              switch (format) {
                case 'geojson':
                case 'application/json':
                  type = 'geojson'
                  break
              }
              const features = createFeatures(result, type)
              resolve(
                features.map(feature => [
                  layerName,
                  geometryName,
                  feature,
                  coords,
                ]),
              )
            }
          })
        }
      })
    })
    const allResults = await Promise.all(promises)
    results.value = allResults.flat(1)
    show(evt)
  }
}

const prepareWMSGetFeatureInfoUrl = (coords, layer) => {
  const queryLayers = layer.getSource().getParams().LAYERS.split(',').reverse()
  const viewResolution = /** @type {number} */ (
    props.map.getView().getResolution()
  )

  return layer
    .getSource()
    .getFeatureInfoUrl(
      coords,
      viewResolution,
      props.map.getView().getProjection(),
      {
        QUERY_LAYERS: queryLayers.join(','),
        FEATURE_COUNT: 3 * queryLayers.length,
      },
    )
}

const prepareWFSGetFeatureUrl = (coords, layer) => {
  const featureTypes = layer.getSource().getParams().LAYERS.split(',')
  const outputFormat = layer.getSource().getParams().INFO_FORMAT
  const proj = layer.getSource().getProjection()
  const viewResolution = /** @type {number} */ (
    props.map.getView().getResolution()
  )
  const extent = buffer(boundingExtent([coords]), 10 * viewResolution)

  const params = {
    service: 'WFS',
    request: 'getFeature',
    version: '1.0.0',
    srsName: typeof proj === 'string' ? proj : proj.getCode(),
    typeNames: featureTypes.join(','),
    outputFormat,
    maxFeatures: 3 * featureTypes.length,
    bbox: extent.join(','),
  }

  const searchParams = new URLSearchParams(params)
  const urls = layer.getSource().getUrls()
  let url = ''
  if (urls.length) {
    url = urls[0]
  }

  return url + '&' + searchParams.toString()
}

const show = event => {
  if (results.value.length) {
    feature.value = results.value[resultsIndex.value][2]
    layer.getSource().addFeature(feature.value)
    title.value = results.value[resultsIndex.value][0]
    const featureProps = feature.value.getProperties()
    delete featureProps.boundedBy
    delete featureProps[results.value[resultsIndex.value][1]]
    properties.value = featureProps

    popup.value.setPosition(results.value[resultsIndex.value][3])
    // open.value = true

    popover.value.show(event, element.value)
  }
}

const hide = () => {
  feature.value = null
  properties.value = {}
  results.value = []
  resultsIndex.value = 0
  open.value = false
  layer.getSource()?.clear()
}

const prev = () => {
  resultsIndex.value = resultsIndex.value > 0 ? resultsIndex.value - 1 : 0
  layer.getSource()?.clear()
  show()
}

const next = () => {
  const lastIndex = results.value.length - 1
  resultsIndex.value =
    resultsIndex.value < lastIndex ? resultsIndex.value + 1 : lastIndex
  layer.getSource()?.clear()
  show()
}

const requestFeatureInfo = (url, cb) => {
  axios
    .get(url)
    .then(result => result.data)
    .then(cb)
    .catch(console.error)
}
</script>

<template>
    <div :id="`map-${id}-popup-wrapper`" class="popup-wrapper" ref="popupWrapper">
    </div>

    <Popover
        append-to="body"
        :baseZIndex="1000"
        ref="popover"
    >
        <div class="mb-0 flex justify-between popup-header">
            <span class="mr-1">{{ title }}</span>
            <span class="tools">
                <span v-if="results.length > 1" class="text-sm">
                    <span class="pi pi-chevron-left" @click="prev()"></span>
                    {{ resultsIndex + 1 }} / {{ results.length }}
                    <span class="pi pi-chevron-right" @click="next()"></span>
                </span>
                <span class="pi pi-times" @click="hide()"></span>
            </span>
        </div>
        <div class="text-sm">
            <div v-for="(value, key) in properties" :key="`map-${id}-popup-list-${key}`" class="flex flex-column gap-2 justify-between">
                <b>{{ key }}</b>
                <span>{{ value }}</span>
            </div>
        </div>
    </Popover>
</template>

<style scoped>
.popup-header {
    line-height: 1.5em;
}
</style>
